import { useCallback, useMemo } from 'react'

import { SelectedInstrument } from '../constants/instruments'
import { DEFAULT_RFQ_SIZE, LegsUnpriced, RFQ_MAX_LEGS } from '../constants/rfqs'
import { getDefaultOrderQuoteParams } from '../utils/order'
import { getRfqGcdAmount } from '../utils/rfqs'
import useOrderOrRfqQueryParams from './useOrderOrRfqQueryParams'

export default function useSelectedInstruments(): {
  selectedInstruments: SelectedInstrument[]
  addInstrument: (selectedInstrument: SelectedInstrument) => void
  removeInstrument: (selectedInstrument: SelectedInstrument) => void
  setInstruments: (selectedInstruments: SelectedInstrument[]) => void
  toggleInstrumentDirection: (selectedInstrument: SelectedInstrument) => void
  getIsInstrumentWithDirectionSelected: (selectedInstrument: SelectedInstrument) => boolean
  getIsInstrumentSelected: (selectedInstrument: string) => boolean
  removeAllInstruments: () => void
} {
  const {
    isRfq,
    setOrderParams,
    setRfqParams,
    instruments: selectedInstruments,
  } = useOrderOrRfqQueryParams()

  const setInstruments = useCallback(
    (selectedInstruments: SelectedInstrument[]) => {
      if (!selectedInstruments.length) {
        if (isRfq) {
          // reset rfq params
          setRfqParams(null)
        } else {
          // reset order params
          setOrderParams(null)
        }
      } else {
        if (selectedInstruments.length > RFQ_MAX_LEGS) {
          return
        }

        if (isRfq || selectedInstruments.length > 1) {
          setRfqParams((params) => {
            const newLegs: LegsUnpriced = selectedInstruments.map((selectedInstrument) => {
              const existingLeg = params.legs.find(
                (leg) =>
                  leg.instrument_name === selectedInstrument.instrument_name &&
                  leg.direction === selectedInstrument.direction
              )
              if (existingLeg) {
                // leg already exists, no-op
                return existingLeg
              } else {
                const amount = getRfqGcdAmount(params.legs)
                return {
                  amount: amount ? amount.toString() : DEFAULT_RFQ_SIZE.toString(),
                  direction: selectedInstrument.direction,
                  instrument_name: selectedInstrument.instrument_name,
                }
              }
            })

            return {
              ...params,
              legs: newLegs,
            }
          })
        } else {
          const selectedInstrument = selectedInstruments[0]
          setOrderParams({
            ...getDefaultOrderQuoteParams(selectedInstrument.instrument_name),
            direction: selectedInstrument.direction,
          })
        }
      }
    },
    [isRfq, setOrderParams, setRfqParams]
  )

  const getIsInstrumentWithDirectionSelected = useCallback(
    (selectedInstrument: SelectedInstrument) => {
      return selectedInstruments.some(
        (option) =>
          option.instrument_name === selectedInstrument.instrument_name &&
          option.direction === selectedInstrument.direction
      )
    },
    [selectedInstruments]
  )

  const getIsInstrumentSelected = useCallback(
    (selectedInstrument: string) => {
      return selectedInstruments.some((option) => option.instrument_name === selectedInstrument)
    },
    [selectedInstruments]
  )

  const addInstrument = useCallback(
    (selectedInstrument: SelectedInstrument) => {
      if (isRfq) {
        // add option

        if (getIsInstrumentSelected(selectedInstrument.instrument_name)) {
          // instrument already selected for opposite direction
          // flip isBuy: filter out selected option, select same instrument for opposite direction
          setInstruments([
            ...selectedInstruments.filter(
              (option) => option.instrument_name !== selectedInstrument.instrument_name
            ),
            selectedInstrument,
          ])
        } else {
          setInstruments([...selectedInstruments, selectedInstrument])
        }
      } else {
        // set to single option
        setInstruments([selectedInstrument])
      }
    },
    [isRfq, selectedInstruments, setInstruments, getIsInstrumentSelected]
  )

  const removeInstrument = useCallback(
    (selectedInstrument: SelectedInstrument) => {
      if (isRfq) {
        const newSelectedInstruments = [...selectedInstruments].filter(
          (option) =>
            !(
              option.instrument_name === selectedInstrument.instrument_name &&
              option.direction === selectedInstrument.direction
            )
        )
        // remove option from list
        setInstruments(newSelectedInstruments)
      } else {
        // empty options
        setInstruments([])
      }
    },
    [isRfq, selectedInstruments, setInstruments]
  )

  const toggleInstrumentDirection = useCallback(
    (selectedInstrument: SelectedInstrument) => {
      if (getIsInstrumentWithDirectionSelected(selectedInstrument)) {
        removeInstrument(selectedInstrument)
      } else {
        addInstrument(selectedInstrument)
      }
    },
    [removeInstrument, getIsInstrumentWithDirectionSelected, addInstrument]
  )

  const removeAllInstruments = useCallback(() => setInstruments([]), [setInstruments])

  return useMemo(
    () => ({
      selectedInstruments,
      addInstrument,
      removeInstrument,
      removeAllInstruments,
      getIsInstrumentWithDirectionSelected,
      getIsInstrumentSelected,
      toggleInstrumentDirection,
      setInstruments,
    }),
    [
      removeAllInstruments,
      removeInstrument,
      getIsInstrumentWithDirectionSelected,
      getIsInstrumentSelected,
      addInstrument,
      selectedInstruments,
      toggleInstrumentDirection,
      setInstruments,
    ]
  )
}
